// components/NewUi/TreatmentFlow/PatientDetails.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { FiGrid, FiCamera, FiUser, FiSettings, FiMic } from "react-icons/fi";
import flow from "./flow.module.scss";
import { toast } from "react-toastify";
import styles from "./patientDetails.module.scss";
import Lottie from "lottie-react";
import generatingAnim from "./animations/Female Employee Working on Data Security.json";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AudioRecorder, {
  ACTIVE_AUDIO_WORKFLOW_EVENT,
  hasGeneratedPatientNoteInAudioRecordData,
  hasActiveAudioWorkflowForTreatmentPlan,
} from "../AudioRecorder/Audio3second";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

const loadCKEditorScript = () =>
  new Promise((resolve, reject) => {
    if (window.CKEDITOR) {
      resolve(window.CKEDITOR);
      return;
    }

    const existingScript = document.querySelector(
      'script[data-ckeditor-script="true"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.CKEDITOR), {
        once: true,
      });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load CKEditor.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.async = true;
    script.dataset.ckeditorScript = "true";
    script.onload = () => resolve(window.CKEDITOR);
    script.onerror = () => reject(new Error("Failed to load CKEditor."));
    document.body.appendChild(script);
  });

const normalizeNoteValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return String(value);
  }
};

const getNotesFromTreatmentData = (data) => {
  const possibleNote =
    data?.notes ||
    data?.patient_notes ||
    data?.patient_note ||
    data?.clinical_notes ||
    data?.transcription_notes ||
    data?.transcript ||
    data?.transcript_text ||
    data?.audio_recording?.patient_note ||
    data?.audio_recording?.notes ||
    data?.audio_recording?.transcript ||
    "";

  return normalizeNoteValue(possibleNote);
};


const escapePatientNoteHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isHtmlPatientNoteValue = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const formatPatientNoteInline = (value) => {
  const escapedValue = escapePatientNoteHtml(String(value || "").trim());

  return escapedValue
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>");
};

const normalizePatientNoteForRichEditor = (value) =>
  String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/^\s*#\s*/, "")
    .replace(
      /\s+\*\*(MH|PCO|SH|O\/e|I\/O|Soft tissues|OH|Gingival health|Hard tissues|Restorations|Crown margins|Toothwear|Abrasion|Abfraction|Acid erosion|Attrition|Occlusion|Mobility|SI|SI for specific tooth|Tooth|TTP|BST|Sinus formation|Colour changes|Diagnosis|Dw pt|Pt has chosen|Risk assessment|Caries risk|Perio risk|Toothwear risk|Recall):\*\*/gi,
      "\n\n**$1:**"
    )
    .replace(/\s+-\s+(?=\*\*[^*]+:\*\*)/g, "\n- ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const splitPatientNoteListItems = (value) => {
  const trimmedValue = String(value || "").trim();
  const hasListMarkers =
    trimmedValue.startsWith("-") || /\s+-\s+(?=\*\*[^*]+:\*\*)/.test(trimmedValue);

  if (!hasListMarkers) return [];

  return trimmedValue
    .split(/\s+-\s+(?=\*\*[^*]+:\*\*)/g)
    .map((item) => item.replace(/^-\s*/, "").trim())
    .filter(Boolean);
};

const renderPatientNoteList = (items) => {
  if (!items.length) return "";

  return `<ul>${items
    .map((item) => `<li>${formatPatientNoteInline(item)}</li>`)
    .join("")}</ul>`;
};

const renderPatientNoteSection = (label, paragraphs = [], listItems = []) => {
  const bodyHtml = [
    ...paragraphs
      .map((paragraph) => String(paragraph || "").trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${formatPatientNoteInline(paragraph)}</p>`),
    renderPatientNoteList(listItems),
  ]
    .filter(Boolean)
    .join("\n");

  return `<section><h4>${escapePatientNoteHtml(label)}:</h4>${bodyHtml}</section>`;
};

const formatPatientNoteHtmlForEditor = (patientNote) => {
  const rawText = normalizeNoteValue(patientNote);

  if (!rawText.trim()) return "";
  if (isHtmlPatientNoteValue(rawText)) return rawText;

  const normalizedText = normalizePatientNoteForRichEditor(rawText);
  const lines = normalizedText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return "";

  const htmlParts = [];
  let pendingListItems = [];
  let currentSectionLabel = "";
  let currentSectionParagraphs = [];
  let currentSectionListItems = [];

  const flushList = () => {
    if (!pendingListItems.length) return;
    htmlParts.push(renderPatientNoteList(pendingListItems));
    pendingListItems = [];
  };

  const flushSection = () => {
    if (!currentSectionLabel) return;

    htmlParts.push(
      renderPatientNoteSection(
        currentSectionLabel,
        currentSectionParagraphs,
        currentSectionListItems
      )
    );
    currentSectionLabel = "";
    currentSectionParagraphs = [];
    currentSectionListItems = [];
  };

  lines.forEach((line, index) => {
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    const numberedMatch = line.match(/^\d+\.\s+(.+)$/);
    const sectionMatch = line.match(/^\*\*([^*]+):\*\*\s*(.*)$/);

    if (headingMatch) {
      flushSection();
      flushList();
      const level = Math.min(4, headingMatch[1].length + 2);
      htmlParts.push(`<h${level}>${formatPatientNoteInline(headingMatch[2])}</h${level}>`);
      return;
    }

    if (index === 0 && !line.startsWith("-") && !numberedMatch && !sectionMatch) {
      flushSection();
      flushList();
      htmlParts.push(`<h3>${formatPatientNoteInline(line)}</h3>`);
      return;
    }

    if (sectionMatch) {
      flushSection();
      flushList();
      currentSectionLabel = sectionMatch[1];

      const body = sectionMatch[2].trim();
      const bodyListItems = splitPatientNoteListItems(body);

      if (bodyListItems.length) {
        currentSectionListItems.push(...bodyListItems);
      } else if (body) {
        currentSectionParagraphs.push(body);
      }

      return;
    }

    if (line.startsWith("-") || numberedMatch) {
      const cleanLine = line.replace(/^-\s*/, "").replace(/^\d+\.\s+/, "").trim();

      if (currentSectionLabel) {
        currentSectionListItems.push(cleanLine);
      } else {
        pendingListItems.push(cleanLine);
      }
      return;
    }

    if (currentSectionLabel) {
      currentSectionParagraphs.push(line);
      return;
    }

    flushList();
    htmlParts.push(`<p>${formatPatientNoteInline(line)}</p>`);
  });

  flushSection();
  flushList();

  return htmlParts.join("\n");
};

const normalizeEditedPatientNoteText = (value) =>
  String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const ckEditorHtmlToMarkdownText = (html) => {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node;
    const tagName = element.tagName.toLowerCase();
    const childText = Array.from(element.childNodes).map(walk).join("");

    if (tagName === "br") return "\n";
    if (tagName === "strong" || tagName === "b") return `**${childText.trim()}**`;
    if (tagName === "em" || tagName === "i") return `*${childText.trim()}*`;
    if (tagName === "li") return `- ${childText.trim()}\n`;

    if (tagName === "ul" || tagName === "ol") {
      return `${childText.trim()}\n\n`;
    }

    if (/^h[1-6]$/.test(tagName)) {
      const level = Number(tagName.slice(1));
      return `${"#".repeat(Math.min(level, 4))} ${childText.trim()}\n\n`;
    }

    if (["p", "div", "section", "blockquote"].includes(tagName)) {
      const cleanText = childText.trim();
      return cleanText ? `${cleanText}\n\n` : "";
    }

    return childText;
  };

  return normalizeEditedPatientNoteText(
    Array.from(container.childNodes).map(walk).join("")
  );
};

const getPatientNoteCKEditorConfig = (minHeight = 300) => {
  const CKEDITOR = window.CKEDITOR;

  return {
    allowedContent: true,
    removePlugins: "resize,elementspath",
    resize_enabled: false,
    height: minHeight,
    extraAllowedContent:
      "section h1 h2 h3 h4 h5 h6 p br ul ol li strong em b i blockquote; *(*)[id,class,style]",
    enterMode: CKEDITOR?.ENTER_P,
    shiftEnterMode: CKEDITOR?.ENTER_BR,
    toolbar: [
      { name: "basicstyles", items: ["Bold", "Italic", "Underline", "RemoveFormat"] },
      { name: "paragraph", items: ["BulletedList", "NumberedList", "Blockquote"] },
      { name: "links", items: ["Link", "Unlink"] },
      { name: "clipboard", items: ["Undo", "Redo"] },
    ],
  };
};

const PlainPatientNoteEditor = ({
  value,
  onChange,
  disabled = false,
  placeholder = "",
  minHeight = 300,
  ariaLabel = "Patient notes editor",
  className = "",
}) => {
  const textareaRef = useRef(null);
  const instanceRef = useRef(null);
  const lastEditorHtmlRef = useRef("");
  const onChangeRef = useRef(onChange);
  const textareaIdRef = useRef(
    `patient-note-ckeditor-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );

  const htmlValue = useMemo(() => formatPatientNoteHtmlForEditor(value), [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;
    const textareaId = textareaIdRef.current;

    const init = async () => {
      if (!textareaRef.current) return;

      try {
        await loadCKEditorScript();
        if (cancelled || !textareaRef.current) return;

        const CKEDITOR = window.CKEDITOR;
        if (!CKEDITOR) return;

        textareaRef.current.id = textareaId;

        if (CKEDITOR.instances?.[textareaId]) {
          CKEDITOR.instances[textareaId].destroy(true);
        }

        const instance = CKEDITOR.replace(
          textareaId,
          getPatientNoteCKEditorConfig(minHeight)
        );

        instanceRef.current = instance;

        instance.on("instanceReady", () => {
          if (cancelled) return;

          instance.setData(htmlValue, {
            callback: () => {
              lastEditorHtmlRef.current = instance.getData();
            },
          });

          instance.setReadOnly(Boolean(disabled));
        });

        instance.on("change", () => {
          const html = instance.getData();
          lastEditorHtmlRef.current = html;
          onChangeRef.current(ckEditorHtmlToMarkdownText(html));
        });
      } catch (error) {
        console.error("Failed to initialise CKEditor:", error);
      }
    };

    init();

    return () => {
      cancelled = true;
      const CKEDITOR = window.CKEDITOR;

      if (CKEDITOR?.instances?.[textareaId]) {
        try {
          CKEDITOR.instances[textareaId].destroy(true);
        } catch (_error) {}
      }

      instanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || instance.status !== "ready") return;

    instance.setReadOnly(Boolean(disabled));
  }, [disabled]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || instance.status !== "ready") return;
    if (instance.focusManager?.hasFocus) return;

    const currentHtml = instance.getData();
    if (currentHtml !== htmlValue && lastEditorHtmlRef.current !== htmlValue) {
      instance.setData(htmlValue, {
        callback: () => {
          lastEditorHtmlRef.current = instance.getData();
        },
      });
    }
  }, [htmlValue]);

  return (
    <div
      className={`${styles.richNoteEditorShell} ${styles.ckEditorShell || ""} ${
        className || ""
      }`.trim()}
      style={{ minHeight }}
      data-placeholder={placeholder}
    >
      <textarea
        ref={textareaRef}
        defaultValue={htmlValue}
        aria-label={ariaLabel}
        disabled={disabled}
      />
    </div>
  );
};


const DEFAULT_AUDIO_UNSAFE_CLOSE_STATE = {
  isUnsafe: false,
  reason: null,
  title: "",
  message: "",
  isRecording: false,
  isUploadInProgress: false,
  isWorkflowInProgress: false,
};

export default function PatientDetails({
  onNext,
  treatmentData,
  setTreatmentData,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const transcriptionModalOpenedRef = useRef(false);
  const aiPatientLetterGeneratorRef = useRef(null);
  const scrolledToEditedPatientNoteRef = useRef("");
  const completedAudioRecordCheckedPlanRef = useRef("");
  const shouldMarkReloadedNoteAsEditedRef = useRef(false);
  const [clinician, setClinician] = useState("");
  const [applyTemplate, setApplyTemplate] = useState(true);
  const [note, setNote] = useState("");
  const [noteEditorVersion, setNoteEditorVersion] = useState(0);
  const [dueOption, setDueOption] = useState("2 weeks");
  const [customDate, setCustomDate] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasEditedNote, setHasEditedNote] = useState(false);
  const [showEmptyNoteModal, setShowEmptyNoteModal] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [showTranscriptionNotesModal, setShowTranscriptionNotesModal] =useState(false);
  const [showTranscriptionSaveConfirmModal, setShowTranscriptionSaveConfirmModal] =useState(false);
  const [dueError, setDueError] = useState("");
  const [showRecordSessionModal, setShowRecordSessionModal] = useState(false);
  const [showAudioCloseConfirmModal, setShowAudioCloseConfirmModal] =useState(false);
  const [audioUnsafeCloseState, setAudioUnsafeCloseState] = useState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);
  const [completedAudioRecordData, setCompletedAudioRecordData] = useState(null);
  const allTreatments = treatmentData?.treatment_plan?.treatments || [];
  const totalPrice = allTreatments.reduce((sum, t) => sum + (t.price || 0), 0);
  const consentBundleId = treatmentData?.id;
  const treatmentPlanId = treatmentData?.treatment_plan_id;

  const isTranscriptionReviewUrl = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("transcription") === "true";
  }, [location.search]);

  const shouldFocusEditedPatientLetter = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const patientNoteStatus = String(params.get("patientNoteStatus") || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    return (
      params.get("from") === "dashboard" &&
      params.get("transcription") === "true" &&
      params.get("focus") === "ai-patient-letter-generator" &&
      patientNoteStatus === "edited"
    );
  }, [location.search]);

  const todayDate = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const showDueError = (message) => {
    setDueError(message);
    setTimeout(() => {
      setDueError("");
    }, 3000);
  };

  const isPastDate = (dateValue) => {
    if (!dateValue) return false;

    const selected = new Date(`${dateValue}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selected < today;
  };

  const getExistingAppointmentDate = () => {
    const possibleDate =
      treatmentData?.appointment_date ||
      treatmentData?.appointmentDate ||
      treatmentData?.appointment?.date ||
      treatmentData?.appointment?.appointment_date ||
      treatmentData?.due_date ||
      "";

    if (!possibleDate) return "";

    return String(possibleDate).split("T")[0].split(" ")[0];
  };

  const getEffectiveAppointmentDate = () => {
    return customDate || getExistingAppointmentDate();
  };

  const validateAppointmentDateBeforeProgress = () => {
    const effectiveDate = getEffectiveAppointmentDate();

    if (effectiveDate && isPastDate(effectiveDate)) {
      showDueError(
        "Appointment date cannot be in the past. Please select today or a future date before continuing."
      );
      toast.error("Please select today or a future appointment date.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (treatmentData?.clinician) {
      setClinician(treatmentData.clinician.id.toString());
    }
  }, [treatmentData]);

  useEffect(() => {
    const shouldMarkReloadAsEdited = shouldMarkReloadedNoteAsEditedRef.current;

    if (!hasEditedNote || shouldMarkReloadAsEdited) {
      const existingNote = getNotesFromTreatmentData(treatmentData);

      if (existingNote) {
        setNote(existingNote);
        setNoteEditorVersion((version) => version + 1);

        if (shouldMarkReloadAsEdited) {
          setHasEditedNote(true);
          shouldMarkReloadedNoteAsEditedRef.current = false;
        }
      }
    }
  }, [treatmentData, hasEditedNote]);

  useEffect(() => {
    if (!isTranscriptionReviewUrl) return;
    if (shouldFocusEditedPatientLetter) return;
    if (transcriptionModalOpenedRef.current) return;
    if (!consentBundleId) return;

    const existingNote = getNotesFromTreatmentData(treatmentData);
    if (existingNote && !hasEditedNote) {
      setNote(existingNote);
      setNoteEditorVersion((version) => version + 1);
    }

    setShowTranscriptionNotesModal(true);
    transcriptionModalOpenedRef.current = true;
  }, [
    isTranscriptionReviewUrl,
    shouldFocusEditedPatientLetter,
    consentBundleId,
    treatmentData,
    hasEditedNote,
  ]);

  useEffect(() => {
    if (!shouldFocusEditedPatientLetter || !consentBundleId) return;

    const scrollKey = String(consentBundleId);
    if (scrolledToEditedPatientNoteRef.current === scrollKey) return;

    let secondAnimationFrame = 0;
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      secondAnimationFrame = window.requestAnimationFrame(() => {
        const target = aiPatientLetterGeneratorRef.current;
        if (!target) return;

        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        scrolledToEditedPatientNoteRef.current = scrollKey;
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame);
      if (secondAnimationFrame) {
        window.cancelAnimationFrame(secondAnimationFrame);
      }
    };
  }, [shouldFocusEditedPatientLetter, consentBundleId, noteEditorVersion]);

  useEffect(() => {
    setShowRecordSessionModal(false);
    setShowAudioCloseConfirmModal(false);
    setCompletedAudioRecordData(null);
    setAudioUnsafeCloseState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);
  }, [treatmentPlanId, consentBundleId]);

  useEffect(() => {
    if (!treatmentPlanId || !consentBundleId) return;

    const restoreActiveAudioModal = () => {
      if (
        hasActiveAudioWorkflowForTreatmentPlan(
          treatmentPlanId,
          consentBundleId
        )
      ) {
        setShowRecordSessionModal(true);
      }
    };

    restoreActiveAudioModal();
    window.addEventListener(
      ACTIVE_AUDIO_WORKFLOW_EVENT,
      restoreActiveAudioModal
    );
    window.addEventListener("storage", restoreActiveAudioModal);

    return () => {
      window.removeEventListener(
        ACTIVE_AUDIO_WORKFLOW_EVENT,
        restoreActiveAudioModal
      );
      window.removeEventListener("storage", restoreActiveAudioModal);
    };
  }, [treatmentPlanId, consentBundleId]);

  useEffect(() => {
    const normalizedTreatmentPlanId = String(treatmentPlanId || "").trim();
    const normalizedConsentBundleId = String(consentBundleId || "").trim();
    if (!normalizedTreatmentPlanId || !normalizedConsentBundleId) return;
    const audioContextKey = `${normalizedTreatmentPlanId}:${normalizedConsentBundleId}`;
    if (
      completedAudioRecordCheckedPlanRef.current === audioContextKey
    ) {
      return;
    }

    const apiBaseUrl = String(
      process.env.REACT_APP_BASE_URL_V2 || ""
    ).replace(/\/+$/, "");
    if (!apiBaseUrl) return;

    setCompletedAudioRecordData(null);
    const abortController = new AbortController();

    const checkForCompletedPatientNote = async () => {
      try {
        const { data } = await axios.get(
          `${apiBaseUrl}/audio-recording/get_audio_record_data/${encodeURIComponent(
            normalizedTreatmentPlanId
          )}`,
          {
            withCredentials: false,
            signal: abortController.signal,
          }
        );

        completedAudioRecordCheckedPlanRef.current = audioContextKey;

        if (
          !hasGeneratedPatientNoteInAudioRecordData(
            data,
            normalizedTreatmentPlanId,
            normalizedConsentBundleId
          )
        ) {
          return;
        }

        setCompletedAudioRecordData(data);
        setShowRecordSessionModal(true);
      } catch (error) {
        if (
          error?.name === "CanceledError" ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }

        completedAudioRecordCheckedPlanRef.current = audioContextKey;
        console.warn(
          "Could not check for a generated patient note when opening the consent bundle:",
          error
        );
      }
    };

    void checkForCompletedPatientNote();

    return () => {
      abortController.abort();
    };
  }, [treatmentPlanId, consentBundleId]);

  const patient = treatmentData?.patient || {};
  const clinicianObj = treatmentData?.clinician || {};

  const reloadTreatmentData = async () => {
    try {
      const res = await axios.get(`consent_bundle/${consentBundleId}`);
      setTreatmentData(res.data);
      console.log("Consent bundle reloaded");
      return res.data;
    } catch (err) {
      console.error("Error reloading consent bundle:", err);
      return null;
    }
  };

  const refreshPricing = async () => {
    if (isUpdatingPrice) return;

    setIsUpdatingPrice(true);

    try {
      const { data } = await axios.patch(
        `/consent_bundle/${consentBundleId}/refresh_treatment_pricing`
      );
      toast.success("Treatment Pricing Updated");

      if (data) {
        setTreatmentData(data);
      }
    } catch (err) {
      console.error("Failed to update treatment pricing", err);
      toast.error("Failed to update treatment pricing");
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  const generatePatientLetter = useMutation(
    async (patientNote) => {
      const cleanPatientNote = String(patientNote ?? note).trim();

      if (!cleanPatientNote) {
        toast.info("No patient note provided  skipping letter generation.");
        onNext();
        return null;
      }

      setIsGenerating(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      let attempt = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempt < maxAttempts) {
        try {
          const base = process.env.REACT_APP_BASE_URL_V2;
          if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

          const endpoint = `${base}consent_bundle/${consentBundleId}/generate_patient_letter`;

          const response = await axios.put(endpoint, {
            patient_notes: cleanPatientNote,
          });

          return response;
        } catch (err) {
          attempt += 1;
          lastError = err;
          console.warn(`Attempt ${attempt} failed, retrying...`);
          toast.error("Failed to generate, retrying...");
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      }

      throw lastError;
    },
    {
      onSuccess: async (response) => {
        if (!response) return;

        toast.success("Patient consent letter generated successfully!");
        console.log("Patient letter generated");
        setHasEditedNote(false);
        setIsGenerating(false);

        try {
          await reloadTreatmentData();
          console.log("Treatment data refreshed after letter generation");
        } catch (err) {
          console.error("Error refreshing data after letter generation:", err);
        }

        onNext();
      },
      onError: (error) => {
        console.error("All retries failed:", error);
        toast.error(
          "Failed to generate the letter after 3 attempts. Proceeding to next step."
        );
        setIsGenerating(false);
        onNext();
      },
      onSettled: () => {
        setIsGenerating(false);
      },
    }
  );

  const saveTranscriptionNotes = useMutation(
    async () => {
      if (!consentBundleId) {
        throw new Error("Missing consent bundle ID.");
      }

      if (!treatmentPlanId) {
        throw new Error("Missing treatment plan ID.");
      }

      const cleanNote = note.trim();

      if (!cleanNote) {
        throw new Error("Notes cannot be empty.");
      }

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${base}consent_bundle/${consentBundleId}/generate_patient_letter`;

      const response = await axios.put(endpoint, {
        patient_notes: cleanNote,
      });

      // await axios.post(
      //   `${base}audio-recording/archive_audio_record/${treatmentPlanId}`
      // );

      return response;
    },
    {
      onSuccess: async () => {
        toast.success("Transcription notes saved successfully.");

        const reviewedNote = note.trim();
        setHasEditedNote(false);
        setShowTranscriptionSaveConfirmModal(false);
        setShowTranscriptionNotesModal(false);

        const refreshedData = await reloadTreatmentData();
        const refreshedNote = getNotesFromTreatmentData(refreshedData);

        setNote(refreshedNote || reviewedNote);
        setNoteEditorVersion((version) => version + 1);
      },
      onError: (error) => {
        console.error("Error saving transcription notes:", error);

        toast.error(
          error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to save transcription notes. Please try again."
        );
      },
    }
  );

  const setPractitioner = useMutation(
    ({ practitioner_id, load_clinician_templates }) =>
      axios.put(
        `/consent_bundle/${consentBundleId}/set_practitioner_for_consent_bundle`,
        { practitioner_id, load_clinician_templates }
      ),
    {
      onSuccess: (_data, variables) => {
        console.log("Practitioner updated successfully");

        if (variables?.should_generate_letter) {
          generatePatientLetter.mutate(variables.patient_notes);
        } else {
          onNext();
        }
      },
      onError: (error) => {
        console.error("Error setting practitioner:", error);
        toast.error("Failed to set clinician. Please try again.");
      },
    }
  );

  const updateAppointment = useMutation(
    (payload) => {
      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${base}consent_bundle/${consentBundleId}/update_appointment_date`;
      return axios.put(endpoint, payload);
    },
    {
      onSuccess: () => {
        console.log("Appointment date updated successfully");
        setDueError("");
        reloadTreatmentData();
      },
      onError: (err) => {
        console.error("Error updating appointment date:", err);
        showDueError("Failed to update appointment date. Please select a valid date.");
      },
    }
  );

  const handleDueOptionChange = (value) => {
    setDueOption(value);
    setCustomDate("");
    setDueError("");

    if (value) {
      updateAppointment.mutate({ auto_appointment_date_in: value });
    }
  };

  const handleCustomDateChange = (value) => {
    setCustomDate(value);
    setDueOption("");

    if (!value) {
      setDueError("");
      return;
    }

    if (isPastDate(value)) {
      showDueError("Please select today or a future date.");
      return;
    }

    setDueError("");
    updateAppointment.mutate({ appointment_date: `${value} 09:00:00` });
  };

  const handleTranscriptionSaveClick = () => {
    if (!consentBundleId) {
      toast.error("Missing consent bundle ID.");
      return;
    }

    if (!note.trim()) {
      toast.error("Please add notes before saving.");
      return;
    }

    setShowTranscriptionSaveConfirmModal(true);
  };

  const handleTranscriptionConfirmedSave = () => {
    setShowTranscriptionSaveConfirmModal(false);
    saveTranscriptionNotes.mutate();
  };

  const handleNext = () => {
    if (!consentBundleId) {
      toast.error("Missing consent bundle ID.");
      return;
    }

    if (!clinician) {
      toast.error("Please select a clinician before proceeding.");
      return;
    }

    if (!validateAppointmentDateBeforeProgress()) {
      return;
    }

    if (!note || note.trim() === "") {
      setShowEmptyNoteModal(true);
      return;
    }

    if (hasEditedNote) {
      setShowConfirmModal(true);
      return;
    }

    setPractitioner.mutate({
      practitioner_id: clinician,
      load_clinician_templates: applyTemplate,
      should_generate_letter: false,
    });
  };

  const handleConfirmProceed = () => {
    const patientNotes = note.trim();

    setShowConfirmModal(false);

    if (!validateAppointmentDateBeforeProgress()) {
      return;
    }

    if (!patientNotes) {
      toast.info("No patient note provided  skipping letter generation.");
      onNext();
      return;
    }

    setPractitioner.mutate({
      practitioner_id: clinician,
      load_clinician_templates: applyTemplate,
      should_generate_letter: true,
      patient_notes: patientNotes,
    });
  };

  const handleCancelProceed = () => {
    setShowConfirmModal(false);
  };

  const isBusy =
    isGenerating ||
    setPractitioner.isPending ||
    generatePatientLetter.isPending ||
    updateAppointment.isPending ||
    saveTranscriptionNotes.isPending;

  const isNextDisabled = isBusy || (customDate && isPastDate(customDate));

  const shouldMinimizeRecordSessionModal =
    audioUnsafeCloseState.isUploadInProgress ||
    audioUnsafeCloseState.isWorkflowInProgress;

  const forceCloseRecordSessionModal = () => {
    setShowAudioCloseConfirmModal(false);
    setShowRecordSessionModal(false);
    setAudioUnsafeCloseState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);
  };

  const minimizeRecordSessionModal = () => {
    setShowAudioCloseConfirmModal(false);
    setShowRecordSessionModal(false);
    setAudioUnsafeCloseState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);

    localStorage.setItem(
      "dashboard_audio_review_focus",
      JSON.stringify({
        focus: true,
        section: "audio-recordings-transcription-review",
        createdAt: new Date().toISOString(),
      })
    );

    navigate(
      "/dashboard?view=transcription&section=audio-recordings&focus=transcription-review#audio-recordings-transcription-review"
    );
  };

  const requestCloseRecordSessionModal = () => {
    if (shouldMinimizeRecordSessionModal) {
      minimizeRecordSessionModal();
      return;
    }

    if (audioUnsafeCloseState.isUnsafe) {
      setShowAudioCloseConfirmModal(true);
      return;
    }

    forceCloseRecordSessionModal();
  };

  const handleRecordSessionOverlayMouseDown = (event) => {
    if (event.target !== event.currentTarget) return;
    requestCloseRecordSessionModal();
  };

  const handleAudioUnsafeStateChange = (nextState) => {
    setAudioUnsafeCloseState(nextState || DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);

    if (!nextState?.isUnsafe) {
      setShowAudioCloseConfirmModal(false);
    }
  };

  useEffect(() => {
    if (!showRecordSessionModal || !audioUnsafeCloseState.isUnsafe) return;

    const unloadMessage =
      audioUnsafeCloseState.message ||
      "Audio recording or upload is still running. Leaving now may interrupt it.";

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = unloadMessage;
      return unloadMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    showRecordSessionModal,
    audioUnsafeCloseState.isUnsafe,
    audioUnsafeCloseState.message,
  ]);

  useEffect(() => {
    if (!showRecordSessionModal) return;

    const handleEscapeClose = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      requestCloseRecordSessionModal();
    };

    window.addEventListener("keydown", handleEscapeClose);

    return () => {
      window.removeEventListener("keydown", handleEscapeClose);
    };
  }, [showRecordSessionModal, audioUnsafeCloseState]);


const handleAudioPatientNoteSaved = async ({
  editedPatientNote,
  audioRecordData,
} = {}) => {
  const savedNote = normalizeNoteValue(editedPatientNote);
  shouldMarkReloadedNoteAsEditedRef.current = true;

  if (audioRecordData) {
    setCompletedAudioRecordData(audioRecordData);
  }

  if (savedNote) {
    setNote(savedNote);
    setNoteEditorVersion((version) => version + 1);
  }

  const refreshedData = await reloadTreatmentData();
  const refreshedNote = getNotesFromTreatmentData(refreshedData);
  const nextNote = refreshedNote || savedNote || note;

  if (nextNote) {
    setNote(nextNote);
  }

  setNoteEditorVersion((version) => version + 1);
  setHasEditedNote(true);
  shouldMarkReloadedNoteAsEditedRef.current = false;
  toast.success(
    "AI patient letter generator reloaded. The updated note will regenerate the letter on the next step."
  );
};

const handleOpenRecordSession = () => {
  setShowRecordSessionModal(true);
};

const handleCopyPatientNote = async () => {
  const patientNote = String(note || "").trim();

  if (!patientNote) {
    toast.info("No patient note available to copy.");
    return;
  }

  try {
    await navigator.clipboard.writeText(patientNote);
    toast.success("Patient note copied successfully.");
  } catch (error) {
    console.error("Could not copy patient note:", error);
    toast.error("Unable to copy the patient note.");
  }
};



  return (
    <div className={styles.page}>
      <section className={styles.bundleInfoCard}>
        <div className={styles.bundleInfoRow}>
          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Consent bundle ID</span>
            <div className={styles.bundleValueRow}>
              <span className={styles.bundleValue}>{consentBundleId || ""}</span>

              {consentBundleId && (
                <button
                  type="button"
                  className={styles.copyIconBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(String(consentBundleId));
                    toast.success("Consent bundle ID copied");
                  }}
                  aria-label="Copy consent bundle ID"
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Treatment plan ID</span>
            <div className={styles.bundleValueRow}>
              <span className={styles.bundleValue}>
                {treatmentData?.treatment_plan_id || ""}
              </span>

              {treatmentData?.treatment_plan_id && (
                <button
                  type="button"
                  className={styles.copyIconBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      String(treatmentData.treatment_plan_id)
                    );
                    toast.success("Treatment plan ID copied");
                  }}
                  aria-label="Copy treatment plan ID"
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Code</span>
            <span className={styles.bundleValue}>{treatmentData?.code || ""}</span>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Version</span>
            <span className={styles.bundleValue}>
              v{treatmentData?.version_no || "2.0.0"}
            </span>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Clinician selection</h3>
        <label className={styles.field}>
          <span className={styles.label}>Select clinician</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={clinician}
              onChange={(e) => setClinician(e.target.value)}
              disabled={isBusy}
            >
              <option value="">Choose a clinician</option>
              {clinicianObj?.id && (
                <option value={clinicianObj.id}>
                  {clinicianObj.name} ({clinicianObj.role})
                </option>
              )}
            </select>
          
          </div>
          <small className={styles.helpText}>
            Templates will be loaded based on what the clinician has created for the
            practice.
          </small>
        </label>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={applyTemplate}
            onChange={(e) => setApplyTemplate(e.target.checked)}
            disabled={isBusy}
          />
          <span>Apply this clinicians consent template</span>
        </label>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Patient information</h3>
        <div className={styles.grid}>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={`${patient.title || ""} ${patient.first_name || ""} ${
                patient.last_name || ""
              }`}
              readOnly
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Mobile</span>
            <input
              className={styles.input}
              value={patient.mobile_phone || "N/A"}
              readOnly
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              value={patient.email_address || "N/A"}
              readOnly
            />
          </label>
          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span className={styles.label}>Address</span>
            <input
              className={styles.input}
              value={
                patient.address_line_1 ||
                patient.address_line_2 ||
                patient.town ||
                patient.postcode ||
                "No address provided"
              }
              readOnly
            />
          </label>
        </div>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Due</h3>
        <label className={styles.field}>
          <span className={styles.label}>Schedule appointment date</span>
          <div className={styles.dueRow}>
            <select
              className={styles.select}
              value={dueOption}
              onChange={(e) => handleDueOptionChange(e.target.value)}
              disabled={isBusy}
            >
              <option value="1 day">1 Day</option>
              <option value="1 week">1 Week</option>
              <option value="2 weeks">2 Weeks</option>
              <option value="3 weeks">3 Weeks</option>
              <option value="1 month">1 Month</option>
            </select>

            <span className={styles.orText}>or</span>

            <input
              type="date"
              className={styles.input}
              value={customDate}
              min={todayDate}
              onChange={(e) => handleCustomDateChange(e.target.value)}
              disabled={isBusy}
            />
          </div>

          {dueError ? (
            <small
              style={{
                color: "#d32f2f",
                display: "block",
                marginTop: "8px",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {dueError}
            </small>
          ) : (
            <small className={styles.helpText}>
              Choose a specific date or let the system calculate automatically.
            </small>
          )}
        </label>
      </section>

      {/* <div className={`${styles.subBlock} ${styles.aiFeatureCard}`}>
        <div
          className={styles.aiFeatureBadge}
          role="img"
          aria-label="AI-powered feature"
          title="AI-powered feature"
        >
          <span className={styles.aiFeatureBadgeIcon} aria-hidden="true">
           <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h4 className={styles.subTitle}>Record session</h4>
            <p
              style={{
                margin: "6px 0 0",
                color: "#64748b",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Record the patient consultation or clinical discussion before generating
              the AI patient letter.
            </p>
          </div>

          <button
            type="button"
            className={flow.btnPrimary}
            onClick={handleOpenRecordSession}
            disabled={isBusy}
            style={{
              background: "#111827",
              minWidth: "160px",
            }}
          >
            Record session  <FiMic />
          </button>
        </div>
      </div> */}

      <div
        ref={aiPatientLetterGeneratorRef}
        className={`${styles.subBlock} ${styles.aiFeatureCard}`}
        style={{ scrollMarginTop: "96px" }}
      >
        <div
          className={styles.aiFeatureBadge}
          role="img"
          aria-label="AI-powered feature"
          title="AI-powered feature"
        >
          <span className={styles.aiFeatureBadgeIcon} aria-hidden="true">
           <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
          </span>
        </div>

        <h4 className={styles.subTitle}>Edit patient Notes</h4>
        <div className={styles.noticeBox}>
          
          <span className={styles.noticeText}>
            No need to worry your data is fully encrypted and handled in accordance with GDPR. We use it only to generate your patient letter.
          </span>
        </div>

        <div className={styles.textareaWrapper}>
          <PlainPatientNoteEditor
            key={`main-note-editor-${noteEditorVersion}`}
            value={note}
            minHeight={300}
            placeholder={`(Add patient note here)
MH: Prev ortho + veneers. Wants whiter, wider smile with lower tooth show. Prefers veneers.
O/E: UL12 crowns, UR12 veneers, Class II div I, full overbite, missing UL3, TMJ click LHS. OH good, mild gingivitis.
Dx/Advice: Local gingivitis  improve OH, 36 m recalls.`}
            ariaLabel="AI patient letter generator patient notes"
            disabled={isBusy}
            onChange={(nextValue) => {
              setNote(nextValue);
              setHasEditedNote(true);
            }}
          />

          {isGenerating && (
            <div className={styles.generatingOverlay}>
              <div className={styles.generatingContainer}>
                <Lottie
                  animationData={generatingAnim}
                  loop
                  autoplay
                  style={{ width: 220, height: 220 }}
                />
                <p className={styles.generatingText}>Generating letter...</p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.badgesRow}>
          <span className={`${styles.badge} ${styles.badgeSoft}`}>
            Version: <strong>v{treatmentData?.version_no || "2.0.0"}</strong>
          </span>
          <span className={`${styles.badge} ${styles.badgeOutline}`}>
            Mode: <strong>{treatmentData?.mode || "Consent_Bundle"}</strong>
          </span>

          <button
            type="button"
            className={`${styles.badge} ${styles.badgeOutline}`}
            onClick={handleCopyPatientNote}
            disabled={!String(note || "").trim()}
            aria-label="Copy patient note"
            title="Copy patient note"
            style={{
              appearance: "none",
              gap: "6px",
              fontFamily: "inherit",
              cursor: String(note || "").trim() ? "pointer" : "not-allowed",
              opacity: String(note || "").trim() ? 1 : 0.55,
            }}
          >
            <ContentCopyOutlinedIcon style={{ fontSize: "15px" }} />
            Copy patient note
          </button>
        </div>
      </div>

      {allTreatments.length > 0 && (
        <div className={styles.subBlock}>
          <h4 className={styles.subTitle}>Treatment Plans</h4>

          <div className={styles.bubbleContainer}>
            {allTreatments.map((t) => (
              <div key={t.id} className={styles.treatmentBubble}>
                <span className={styles.bubbleTitle}>{t.nomenclature}</span>

                {t.tooth && t.tooth !== "{}" && (
                  <span className={styles.bubbleTooth}>{t.tooth}</span>
                )}

                <span className={styles.bubblePlan}>
                  Payment Plan: {t.payment_plan}
                </span>

                <span className={styles.bubblePrice}>£{t.price}</span>
              </div>
            ))}
          </div>

          <div className={styles.totalPriceUnderBubbles}>
            <span>Total: </span>
            <strong>£{totalPrice.toFixed(2)}</strong>
          </div>
        </div>
      )}

      {!isGenerating && (
        <div className={styles.footer}>
          <button
            type="button"
            className={`${flow.btnOutline} ${styles.refreshPricingButton}`}
            onClick={refreshPricing}
            disabled={isUpdatingPrice}
          >
            {isUpdatingPrice
              ? "Refreshing..."
              : "Refresh Treatment Pricing"}
          </button>

          <button
            className={`${flow.btnPrimary} ${styles.nextButton}`}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {setPractitioner.isPending ? "Saving clinician..." : "Next"}
          </button>
        </div>
      )}

      {/* {showTranscriptionNotesModal && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modal}
            style={{
              maxWidth: "900px",
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
              textAlign: "left",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "14px",
                marginBottom: "16px",
                paddingBottom: "14px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Review transcription notes</h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    color: "#64748b",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Review and edit the notes before saving them to the patient letter
                  generator.
                </p>
              </div>

              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowTranscriptionNotesModal(false)}
                disabled={saveTranscriptionNotes.isPending}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                borderRadius: "999px",
                background: "#f3f4f6",
                color: "#111827",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "12px",
              }}
            >
              <span>Consent bundle ID:</span>
              <strong>{consentBundleId || ""}</strong>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Patient notes</span>
              <PlainPatientNoteEditor
                key={`transcription-note-editor-${noteEditorVersion}`}
                value={note}
                minHeight={320}
                placeholder="Transcription notes will appear here."
                ariaLabel="Review transcription patient notes"
                className={styles.transcriptionRichNoteEditor}
                disabled={saveTranscriptionNotes.isPending}
                onChange={(nextValue) => {
                  setNote(nextValue);
                  setHasEditedNote(true);
                }}
              />
            </div>

            <div className={styles.modalActions} style={{ marginTop: "18px" }}>
              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowTranscriptionNotesModal(false)}
                disabled={saveTranscriptionNotes.isPending}
              >
                Cancel
              </button>

              <button
                type="button"
                className={flow.btnPrimary}
                onClick={handleTranscriptionSaveClick}
                disabled={saveTranscriptionNotes.isPending || !note.trim()}
              >
                {saveTranscriptionNotes.isPending
                  ? "Saving..."
                  : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {showTranscriptionSaveConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4 className={styles.title}>Confirm save</h4>

            <p className={styles.message}>
              This will save the reviewed transcription notes and regenerate the
              patient consent letter using the updated notes.
              <br />
              <br />
              Do you want to continue?
            </p>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowTranscriptionSaveConfirmModal(false)}
                className={flow.btnOutline}
                disabled={saveTranscriptionNotes.isPending}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleTranscriptionConfirmedSave}
                className={flow.btnPrimary}
                disabled={saveTranscriptionNotes.isPending}
              >
                {saveTranscriptionNotes.isPending
                  ? "Saving..."
                  : "Yes, Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4 className={styles.title}>Confirm change</h4>

            <p className={styles.message}>
              You edited the patient notes. Continuing will regenerate the consent
              letter and update its content based on your changes.
              <br />
              <br />
              Would you like to create a new updated letter?
            </p>

            <div className={styles.modalActions}>
              <button onClick={handleCancelProceed} className={flow.btnOutline}>
                Cancel
              </button>

              <button
                className={flow.btnOutline}
                onClick={() => {
                  if (!validateAppointmentDateBeforeProgress()) {
                    return;
                  }

                  setShowEmptyNoteModal(false);
                  onNext();
                }}
              >
                Proceed without letter generation
              </button>

              <button onClick={handleConfirmProceed} className={flow.btnPrimary}>
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmptyNoteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4>No patient note</h4>

            <p>
              There is no information to generate a patient letter.
              <br />
              Add a note now or continue without generating a letter.
            </p>

            <button
              onClick={() => {
                setShowEmptyNoteModal(false);
                setShowExampleModal(true);
              }}
              className={styles.exampleBtn}
            >
              <InfoOutlinedIcon className={styles.infoPulseIcon} />
              <span>View example patient note</span>
            </button>

            <div className={styles.reassureBox}>
              <InfoOutlinedIcon className={styles.reassureIcon} />
              <span>
                You can always come back and generate the patient letter later if
                needed.
              </span>
            </div>

            <div className={styles.modalActions}>
              <button
                className={flow.btnOutline}
                onClick={() => {
                  setShowEmptyNoteModal(false);
                  const el = document.querySelector(`.${styles.richNoteEditor}`);
                  if (el) el.focus();
                }}
              >
                Add note
              </button>

              <button
                className={flow.btnPrimary}
                onClick={() => {
                  if (!validateAppointmentDateBeforeProgress()) {
                    setShowEmptyNoteModal(false);
                    return;
                  }

                  setShowEmptyNoteModal(false);
                  onNext();
                }}
              >
                Proceed without letter
              </button>
            </div>
          </div>
        </div>
      )}

      {showExampleModal && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modal}
            style={{
              maxWidth: "650px",
              maxHeight: "75vh",
              overflowY: "auto",
              padding: "22px",
              borderRadius: "14px",
              background: "#fff",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                margin: "0 0 14px 0",
                fontWeight: 600,
                fontSize: "18px",
                textAlign: "left",
              }}
            >
              Example patient note
            </h3>

            <p
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.55",
                fontSize: "14px",
                color: "#444",
                background: "#f9fafb",
                padding: "16px",
                borderRadius: "10px",
                border: "1px solid #eee",
                textAlign: "left",
              }}
            >
              <strong>MH:</strong> Checked PCO: had a lot of treatment in the past,
              such as ortho as a child, veneers as an adult. Wants a better smile as
              he hasnt reached his ideal look. Lower teeth dont show; prefers lower
              tooth display. Wants a wider and whiter smile. Avoids Invisalign (long
              treatment) and composite bonding (short lifespan). Interested in veneers.
              Aware LLQ bridge may be an issue.
              {"\n\n"}
              <strong>SH:</strong> NAD
              {"\n\n"}
              <strong>O/E:</strong> UL12 crowns, UR12 veneers, incisal wear, Class II
              div I, heavy overbite with no lower tooth display. Narrow smile, UL3
              missing. TMJ click on LHS. Oral cancer screen: NAD. OH: Good. Mild
              gingivitis.
              {"\n\n"}
              <strong>Diagnosis:</strong> Chronic localised gingivitis.
              {"\n\n"}
              <strong>Advice:</strong> OHIG  improve brushing time + add floss/TePe
              brushes at night. Recommend 36 month cleans.
              {"\n\n"}
              <strong>Patient has chosen:</strong> Veneers.
            </p>

            <div
              className={styles.modalActions}
              style={{ justifyContent: "flex-start", marginTop: "16px" }}
            >
              <button
                className={flow.btnPrimary}
                onClick={() => setShowExampleModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecordSessionModal && (
        <div
          className={styles.modalOverlay}
          onMouseDown={handleRecordSessionOverlayMouseDown}
        >
          <div
            className={styles.modal}
            style={{
              maxWidth: "900px",
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "14px",
                paddingBottom: "12px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Record session</h3>

                <div
                  style={{
                    marginTop: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 10px",
                    borderRadius: "999px",
                    background: "#f3f4f6",
                    color: "#111827",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <span>Consent bundle ID:</span>
                  <strong>{consentBundleId || ""}</strong>
                </div>
              </div>

              <button
                type="button"
                className={
                  shouldMinimizeRecordSessionModal
                    ? styles.recordSessionMinimizeButton
                    : flow.btnOutline
                }
                style={
                  shouldMinimizeRecordSessionModal
                    ? {
                        width: "32px",
                        height: "28px",
                        minWidth: "32px",
                        padding: 0,
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: "#e5e7eb",
                        color: "#111827",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                        fontWeight: 700,
                        lineHeight: 1,
                        cursor: "pointer",
                      }
                    : undefined
                }
                onClick={requestCloseRecordSessionModal}
                aria-label={
                  shouldMinimizeRecordSessionModal
                    ? "Minimize recording modal and go to dashboard"
                    : "Close recording modal"
                }
                title={
                  shouldMinimizeRecordSessionModal
                    ? "Minimize and go to dashboard"
                    : "Close"
                }
              >
                {shouldMinimizeRecordSessionModal ? (
                  <span aria-hidden="true" style={{ transform: "translateY(-2px)" }}>
                    -
                  </span>
                ) : (
                  "Close"
                )}
              </button>
            </div>

            <AudioRecorder
              key={`audio-recorder-${treatmentPlanId}-${consentBundleId}`}
              consentBundleId={consentBundleId}
              treatmentPlanId={treatmentPlanId}
              initialAudioRecordData={completedAudioRecordData}
              apiBaseUrl={process.env.REACT_APP_BASE_URL_V2}
              onRequestClose={forceCloseRecordSessionModal}
              onUnsafeStateChange={handleAudioUnsafeStateChange}
              onPatientNoteSaved={handleAudioPatientNoteSaved}
            />
          </div>
        </div>
      )}

      {showRecordSessionModal && showAudioCloseConfirmModal && (
        <div
          className={styles.modalOverlay}
          style={{ zIndex: 9999 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowAudioCloseConfirmModal(false);
            }
          }}
        >
          <div
            className={styles.modal}
            style={{
              maxWidth: "460px",
              width: "92%",
              textAlign: "left",
            }}
          >
            <h4 className={styles.title}>
              {audioUnsafeCloseState.title || "Audio workflow in progress"}
            </h4>

            <p className={styles.message}>
              {audioUnsafeCloseState.message ||
                "Audio recording or upload is still running. Closing now may interrupt it."}
            </p>

            <div className={styles.reassureBox}>
              <InfoOutlinedIcon className={styles.reassureIcon} />
              <span>
                Choose Stay here to continue safely, or Close anyway if you are sure.
              </span>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowAudioCloseConfirmModal(false)}
              >
                Stay here
              </button>

              <button
                type="button"
                className={flow.btnPrimary}
                style={{ background: "#dc2626" }}
                onClick={forceCloseRecordSessionModal}
              >
                Close anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
